// crafting-waiver.ts — crafting_waives_nonmagical_reagents (Wizard v9, Transmutation L35 "Universal Crafter", 2026-10-09):
// "You can craft with any material as if it were appropriate, as long as the material required is not magical."
//
// Owner definition (2026-10-09): a requirement is MAGICAL when its requires_reagent entry carries an ENCHANTMENT TEMPLATE (the
// `uuid|template` part). Every UNtemplated requirement of a crafting recipe is waived for a crafter who holds the flag: the reagent gate
// (ability-preconditions.ts) does not require it and the craft start (crafting-start.ts) does not consume or record it. Templated
// requirements behave as before. The wider magical / non-magical definition is loose on purpose — no new tagging (owner).
//
// Browser only (owner ruling 2026-10-09, no PHP rule copy): the server persists the consumption list the page decided.
import { casterLayerProvider } from './cast-range-gate';
import type { AbilityPreconditionReaders } from './ability-preconditions';

type Row = Record<string, unknown>;

export const CRAFTING_WAIVES_NONMAGICAL_REAGENTS = 'crafting_waives_nonmagical_reagents';

/** An int_0_1 flag reads ON for any non-zero value (a stored 0 is an explicit off). */
function flagOn(value: unknown): boolean {
  const raw = Array.isArray(value) ? value[0] : value;
  if (raw === null || raw === undefined || raw === '' || raw === false) return false;
  const n = Number(raw);
  return Number.isFinite(n) ? n !== 0 : raw === true;
}

/** Whether the crafter holds the flag — on its root, an active ability bucket entry, a feat (or its grants) or an active status
 *  (casterLayerProvider's walk). true / false; null = a provider the page cannot resolve might hold it (the caller stands down). */
export function craftingWaivesNonmagicalReagents(read: AbilityPreconditionReaders, caster: Row): boolean | null {
  const provider = casterLayerProvider(read, caster, CRAFTING_WAIVES_NONMAGICAL_REAGENTS,
    row => flagOn(read.value(row, CRAFTING_WAIVES_NONMAGICAL_REAGENTS)));
  if (provider === undefined) return null;
  return provider !== null;
}

/** The owner's test: a requirement is magical when it names an enchantment template. */
export function reagentRequirementIsMagical(template: string | null | undefined): boolean {
  return typeof template === 'string' && template.trim() !== '';
}

/** Split requirements into the ones still required and the ones a Universal Crafter waives (the untemplated ones). */
export function waiveNonmagicalRequirements<T extends { template: string | null }>(rows: readonly T[]): { kept: T[]; waived: T[] } {
  const kept: T[] = [];
  const waived: T[] = [];
  for (const row of rows) (reagentRequirementIsMagical(row.template) ? kept : waived).push(row);
  return { kept, waived };
}
